// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { EMPTY, forkJoin, merge, Observable } from 'rxjs';
import { ApiService, BaseApiService } from '@fc-core/services/base-api.service';
import {
  MaintenanceAlert,
  MaintenanceAlertStatus,
  MaintenanceSchedule,
} from '@fc-core/models';
import { HttpClient, HttpParams } from '@angular/common/http';
import { expand, map, switchMap } from 'rxjs/operators';
import { MaintenanceAttachment } from './maintenance-note-api.service';

export interface ClosingPayload {
  notes: string;
  closed_at: string;
  photo: any;
  closed_engine_hours: number;
  closed_odometer: number;
}

export interface AlertUpdatePayload {
  alertId: number;
  vehicleId: number;
  maintenanceId: number;
  note: string;
  files?: File[];
  attachments?: MaintenanceAttachment[];
  deletedAttachments?: MaintenanceAttachment[];
}

@Injectable({ providedIn: 'root' })
@ApiService({})
export class MaintenanceAlertApiService extends BaseApiService {
  constructor(private http: HttpClient) {
    super();
  }

  static fromJSON(res: any): MaintenanceAlert {
    if (res.lastProcessed) {
      res.lastProcessed = new Date(res.lastProcessed);
    }
    if (res.completedAt) {
      res.completedAt = new Date(res.completedAt);
    }
    return res;
  }

  closeAlert(
    schedule: MaintenanceSchedule,
    alert: MaintenanceAlert,
    closing: ClosingPayload,
  ): Observable<MaintenanceAlert> {
    const closingPayload = new FormData();
    Object.keys(closing).forEach((key) => {
      if (closing[key as keyof ClosingPayload]) {
        closingPayload.append(key, closing[key as keyof ClosingPayload] as any);
      }
    });
    return this.http
      .put(this.getAlertCloseUrl(schedule, alert), closingPayload)
      .pipe(map(MaintenanceAlertApiService.fromJSON));
  }

  addMaintenanceAttachments(
    files: File[],
    maintenance: MaintenanceAlert,
  ): Observable<any> {
    return forkJoin(
      files.map((file) => this.addMaintenanceAttachment(file, maintenance)),
    );
  }

  addMaintenanceAttachment(
    file: File,
    maintenance: MaintenanceAlert,
  ): Observable<MaintenanceAttachment> {
    const formData = new FormData();
    formData.append('original', file);
    return this.http.post(
      `${this.getBaseUrl()}/files/maintenance_alerts/${
        maintenance.id
      }/attachments/`,
      formData,
    );
  }

  // TODO: replace hand-rolled pagination loop with fetchAllPages (@fc-shared/utils/request-utils/fetch-all-pages)
  loadAll(
    schedule: MaintenanceSchedule,
    opt?: { status?: MaintenanceAlertStatus },
  ): Observable<MaintenanceAlert[]> {
    return this.http
      .get(this.getAlertsUrl(schedule), { params: this.getParams(opt) })
      .pipe(
        expand((results: any) =>
          !!results['next'] ? this.http.get(results['next']) : EMPTY,
        ),
        map((res: any) => res['results']),
        map((res): MaintenanceAlert[] => {
          if (res) {
            return res.map(MaintenanceAlertApiService.fromJSON);
          }
        }),
      );
  }

  updateAlertNote(
    alertPayload: AlertUpdatePayload,
  ): Observable<MaintenanceAlert> {
    const formData = new FormData();
    formData.append('notes', alertPayload.note || '');
    return this.http.put<MaintenanceAlert>(
      `${this.getBaseUrl()}/maintenance/vehicle/${
        alertPayload.vehicleId
      }/schedule/${alertPayload.maintenanceId}/alerts/${alertPayload.alertId}/`,
      formData,
    );
  }

  updateAlert(alertPayload: AlertUpdatePayload): Observable<MaintenanceAlert> {
    const requests: Observable<any>[] = [];
    if (alertPayload.files.length) {
      requests.push(this.addAttachmentsToAlert(alertPayload));
    }
    if (alertPayload.deletedAttachments?.length) {
      requests.push(
        this.deleteManyAttachments(
          alertPayload.alertId,
          alertPayload.deletedAttachments,
        ),
      );
    }
    if (requests.length === 0) return this.updateAlertNote(alertPayload);
    return this.updateAlertNote(alertPayload).pipe(
      switchMap((alert) => {
        return merge(forkJoin(requests)).pipe(
          map((attachments: any) => {
            attachments = attachments.reduce((a: any, b: any) => a.concat(b));
            if (alertPayload.deletedAttachments.length) {
              return {
                ...alert,
                files: alert.files.filter(
                  (i) =>
                    !alertPayload.deletedAttachments
                      .map((attachment) => attachment.id)
                      .includes(i.id),
                ),
              };
            } else {
              return {
                ...alert,
                files: [...attachments, ...alert.files],
              };
            }
          }),
        );
      }),
    );
  }

  addAttachmentsToAlert(payload: AlertUpdatePayload) {
    return forkJoin(
      payload.files.map((file) =>
        this.addAttachmentToAlert(file, payload.alertId),
      ),
    );
  }

  deleteManyAttachments(alertId: number, attachments: MaintenanceAttachment[]) {
    return forkJoin(
      attachments.map((attachment) =>
        this.deleteAttachment(alertId, attachment.id),
      ),
    );
  }

  deleteAttachment(alertId: number, attachmentId: number) {
    return this.http.delete(
      `${this.getBaseUrl()}/files/maintenance_alerts/${alertId}/attachments/${attachmentId}/`,
    );
  }

  addAttachmentToAlert(file: File, alertId: number) {
    const formData = new FormData();
    formData.append('original', file);
    return this.http.post(
      `${this.getBaseUrl()}/files/maintenance_alerts/${alertId}/attachments/`,
      formData,
    );
  }

  getParams(opt?: { status?: MaintenanceAlertStatus }): HttpParams {
    let params: HttpParams = new HttpParams();
    if (opt && opt.status) {
      params = params.set('status', opt.status.toString());
    }
    return params;
  }

  protected getAlertsUrl(schedule: MaintenanceSchedule): string {
    return `${this.getBaseUrl()}/maintenance/vehicle/${
      schedule.vehicle
    }/schedule/${schedule.id}/alerts/`;
  }

  protected getAlertUrl(
    schedule: MaintenanceSchedule,
    alert: MaintenanceAlert,
  ): string {
    return `${this.getAlertsUrl(schedule)}${alert.id}/`;
  }

  private getAlertCloseUrl(
    schedule: MaintenanceSchedule,
    alert: MaintenanceAlert,
  ) {
    return `${this.getAlertUrl(schedule, alert)}close/`;
  }
}
